import React, { useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import clsx from 'clsx';
import { useForm } from 'react-hook-form';

import { useFilters } from '@/hooks';
import Icons from '@/const/icons';
import { Input } from '@/components/form';
import { Combobox, Options } from '@/components/ui/combobox';
import {
	Button,
	Icon,
	LabelShowingResults,
	Typography,
} from '@/components/common';
import { MobileFilters } from './MobileFilters';
import { useRouter } from 'next/router';
import AppRoutes from '@/const/routes';

export interface FiltersOption {
	[key: string]: Options[] | JSX.Element;
}
interface Props {
	total: number;
	totalItems: number;
	cases: Options[];
	filters?: FiltersOption;
	placeHolderSelect?: string;
	setOpenNewMessage: (value: boolean) => void;
}

interface ItemsSelected {
	[key: string]: any[];
}

export const InboxHeadFilters: React.FC<Props> = ({
	total,
	cases,
	filters,
	totalItems,
	setOpenNewMessage,
	placeHolderSelect = 'View All',
}) => {
	const { register } = useForm();
	const { searchFilter, setParamFilter } = useFilters();
	const { replace } = useRouter();

	const searchParams = useSearchParams();
	const router = useRouter();
	const { caseId } = router.query;
	const params = new URLSearchParams(searchParams);
	const pathname = usePathname();

	const [open, setOpen] = useState(false);
	const [filtersOptions, setFiltersOptions] = useState<Options[]>([]);
	const [filterSelected, setFilterSelected] = useState<string | undefined>();

	const totalRecords = React.useRef(null);

	React.useEffect(() => {
		if (totalRecords.current === null && totalItems) {
			(totalRecords.current as number | null) = totalItems;
		}
	}, [totalItems]);

	const handleChangeLawyer = (value: string | undefined): void => {
		setFilterSelected(value);
		setParamFilter('view', value === 'view-all' ? undefined : value);
	};

	const handleChange = (id: string | undefined): void => {
		id && AddOrRemoveClearAllOption(id);
		if (id !== 'clear-all') {
			setFilterSelected(id);
			if (id) {
				params.set('caseId', id);
			} else {
				params.delete('caseId');
			}
			replace(`${pathname}?${params.toString()}`);
		} else {
			setFilterSelected(undefined);
			replace(`${pathname}`);
		}
	};

	const handleApplyFilter = (selectedFilters: ItemsSelected) => {
		Object.entries(selectedFilters).forEach(([key, value]) =>
			setParamFilter(key.toLowerCase(), value.join(','))
		);
	};

	const AddOrRemoveClearAllOption = (option: string) => {
		const filterCopy = [...filtersOptions];
		if (option === 'clear-all') {
			filterCopy.shift();
		} else if (!filterCopy.some((item) => item.value === 'clear-all')) {
			filterCopy.unshift({
				label: 'Clear All',
				value: 'clear-all',
				icon: <Icon src={Icons.clear} className="!h-5 !w-5 text-primary" />,
			});
		}
		setFiltersOptions(filterCopy);
	};

	React.useEffect(() => {
		// setFilterSelected(caseId as string);
		setFiltersOptions(() => {
			if (caseId) {
				return [
					{
						label: 'Clear All',
						value: 'clear-all',
						icon: <Icon src={Icons.clear} className="!h-5 !w-5 text-primary" />,
					},
					...cases,
				];
			} else return cases;
		});
	}, [cases, caseId]);

	return (
		<div className="flex items-center justify-between mb-4">
			<div className="flex-1 hidden md:block">
				<Button
					size="medium"
					decoration="secondary"
					icon={Icons.arrowLeft}
					className="!py-4 !px-4 !w-44 bg-white shadow-[0px_4px_8px_0px_rgba(0,0,0,0.32)] md:shadow-none"
					onClick={() => setOpenNewMessage(true)}
				>
					<Typography
						type="semibold-16"
						className="flex items-center justify-center gap-3 w-full"
					>
						<Icon src={Icons.edit} className="!w-4 !h-4" />
						New Message
					</Typography>
				</Button>
			</div>
			<div className="flex justify-between md:justify-end items-center gap-3 w-full ml-3 min-w">
				<div
					className={clsx('flex-1 3xl:flex-none', {
						'!flex-none': pathname.includes(AppRoutes.LAWYERAPP),
					})}
				>
					<Input
						name="search"
						register={register}
						placeholder="Search"
						iconLeft={Icons.search}
						className="w-full"
						classNameIcon={'!top-[23%] !w-4 !h-4 text-opacity-black'}
						classNameInput={clsx(
							'pl-9 w-full max-w-[215px] lg:max-w-full 3xl:w-72 !bg-gray-15 !h-[33px] !m-0 border-none',
							{
								'!w-full xl:!w-72': pathname.includes(AppRoutes.LAWYERAPP),
							}
						)}
						onChange={({ target }) => searchFilter(target.value)}
					/>
				</div>
				<div className="flex-1 xl:flex-none">
					<Combobox
						open={open}
						placeholder={placeHolderSelect}
						setOpen={setOpen}
						options={filtersOptions}
						value={filterSelected}
						classNameTrigger="!max-w-[155px] lg:!w-[155px]"
						classNameButton={clsx(
							'lg:!w-[155px] !justify-between !max-w-[155px] min-w-0'
						)}
						classNamePopoverContent="!w-[315px] max-h-[300px] overflow-y-auto scroll-custom"
						setValue={
							pathname.includes(AppRoutes.CLIENTAPP)
								? handleChange
								: handleChangeLawyer
						}
					/>
				</div>
				{filters && (
					<div
						className={clsx('w-fit block md:hidden', {
							hidden: pathname.includes(AppRoutes.CLIENTAPP),
						})}
					>
						<MobileFilters onClickApply={handleApplyFilter} filters={filters} />
					</div>
				)}
				<div className="hidden md:block">
					<LabelShowingResults
						total={total}
						totalItems={totalItems}
						labelTotalItems={
							totalItems && totalItems === 1 ? 'email' : 'emails'
						}
					/>
				</div>
			</div>
		</div>
	);
};
